import React, {
  useContext,
  useEffect,
  useState,
  PropsWithChildren
} from "react";
import {
  getAuth,
  getIdToken,
  getIdTokenResult,
  onAuthStateChanged,
  NextOrObserver,
  User,
  onIdTokenChanged,
  signOut
} from "firebase/auth";
import { getFirebase } from "../api/firebase";
import { Redirect, useLocation } from "@reach/router";

export type AuthContextType =
  | {
      isSignedIn: false;
      isAdmin: false;
      user: null;
    }
  | {
      isSignedIn: true;
      isAdmin: boolean;
      user: User;
      getIdToken: (forceRefresh?: boolean) => Promise<string>;
      onIdTokenChanged: (nextOrObserver: NextOrObserver<User>) => () => void;
      signOut: () => Promise<void>;
    };

export const AuthContext = React.createContext<AuthContextType>({
  isSignedIn: false,
  isAdmin: false,
  user: null
});

const isDev = import.meta.env.DEV;
console.log({ isDev });

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [ctx, setCtx] = useState<AuthContextType>(() => {
    getFirebase();
    const auth = getAuth();

    if (auth.currentUser) {
      const user = auth.currentUser;
      return {
        isSignedIn: true,
        isAdmin: false,
        user,
        getIdToken: (forceRefresh) => getIdToken(user, forceRefresh),
        onIdTokenChanged: (nextOrObserver: NextOrObserver<User>) =>
          onIdTokenChanged(auth, nextOrObserver),
        signOut: () =>
          signOut(auth).then(() => {
            window.location.href = "/";
          })
      };
    }
    return {
      isSignedIn: false,
      isAdmin: false,
      user: null
    };
  });

  useEffect(() => {
    getFirebase();
    const auth = getAuth();

    return onAuthStateChanged(auth, async (user) => {
      console.log("onAuthStateChanged", { user });
      let isAdmin = false;
      if (user) {
        const decodedToken = await getIdTokenResult(user);
        console.log({ decodedToken });
        if (decodedToken.claims.admin) {
          isAdmin = true;
        }
        console.log(import.meta.env.DEV);
        if (import.meta.env.DEV) {
          (window as any).revokeToken = async function () {
            const token = await getIdToken(user);
            fetch("/api/__revoke-token", {
              method: "POST",
              headers: {
                authorization: `Bearer ${token}`
              }
            });
          };
        }
      }
      setCtx(
        user
          ? {
              isSignedIn: true,
              isAdmin,
              user,
              getIdToken: () => getIdToken(user),
              onIdTokenChanged: (nextOrObserver: NextOrObserver<User>) =>
                onIdTokenChanged(auth, nextOrObserver),
              signOut: () =>
                signOut(auth).then(() => {
                  window.location.href = "/";
                })
            }
          : {
              isSignedIn: false,
              isAdmin: false,
              user
            }
      );
    });
  }, []);
  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  return useContext(AuthContext);
}

function stripOrigin(url: URL) {
  const href = url.href;
  return href.replace(url.origin, "");
}

export function requiresAuth<P>(
  Comp: React.ComponentType<P>
): React.ComponentType<P> {
  const NewComp = (props: P) => {
    const { isSignedIn } = useAuth();
    const location = useLocation();
    const url = new URL(location.href);
    const signInUrl = new URL("/sign-in", url);
    signInUrl.searchParams.set("return_to", stripOrigin(url));
    return isSignedIn ? (
      <Comp {...props} />
    ) : (
      <Redirect to={stripOrigin(signInUrl)} noThrow />
    );
  };
  NewComp.displayName = `requiresAuth(${Comp.displayName || "Component"})`;
  return NewComp;
}
